import { useEffect } from 'react';

import { useConfigContext } from '@omq/shared';

import { URLStore } from '../stores/url-store';
import type { HelpQuestion } from '../api/help';

/**
 * Redirect stale question deep links to the base FAQ URL.
 *
 * When the URL contains a question ID but the question could not be
 * resolved on initialization (deleted, or no longer publicly
 * accessible), the server falls back to the default help page while
 * the stale URL stays in the address bar. Replacing the location with
 * the clean base URL makes crawlers treat the stale URL as a
 * soft-404 and drop it from the index.
 *
 * Only question IDs read from the URL trigger the redirect — IDs from
 * the `data-question` embed attribute must not, as the redirect
 * target would re-read the attribute on reload and loop forever.
 *
 * @param {HelpQuestion | null} question - initially resolved question
 */
export const useStaleUrlRedirect = (question: HelpQuestion | null): void => {
  const config = useConfigContext();

  useEffect(() => {
    // URL integration disabled — nothing to redirect
    if (!config.isUrlSyncActive) {
      return;
    }

    // only redirect when the question ID came from the URL
    const urlParams = URLStore.readURLParams(config.urlPatternType);
    if (urlParams.question == null) {
      return;
    }

    // question resolved — deep link is valid
    if (question != null) {
      return;
    }

    // full navigation (not history API) so crawlers register the
    // stale URL as redirected (soft-404)
    window.location.replace(URLStore.buildRootUrl(config.urlPatternType));

    // mount-only: the initial question prop never changes within a mount
    // (a remount, e.g. via the public update() API, re-runs the check
    // against the then-current URL and freshly resolved question)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
